/**
 * The unsubscribe link from an outreach-sequence email/SMS - matches the implementation
 * plan's §5a requirement exactly: GET (loading this page) only shows a confirmation, the
 * actual unsubscribe happens on POST, fired only by an explicit button click.
 *
 * Deliberately two-step, not one-click: a bare "unsubscribes on load" page is a known
 * footgun, because corporate email-security scanners and Outlook Safe Links pre-fetch every
 * link in an email BEFORE the recipient ever sees it - which would silently unsubscribe
 * people who never clicked anything.
 *
 * The channel shown in the confirmation copy is read by decoding the token's payload
 * client-side (jwt-decode, same library and same "malformed = show the generic case"
 * posture as utils/session.ts's readSessionClaims) - NOT verified here. Verification only
 * ever happens server-side, in OutreachUnsubscribeService, when the POST actually fires.
 * This decode is purely for display copy; a tampered or garbled token still reaches the
 * backend unchanged and is rejected there.
 */

import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { Button, Result, Spin } from 'antd';
import StandaloneShell from '../../../selfserve/components/standalone-shell';
import { postUnsubscribe } from '../../api/unsubscribe';
import styles from './index.module.css';

type Status = 'confirming' | 'submitting' | 'done' | 'error';

interface UnsubscribeTokenPayload {
  channel?: 'EMAIL' | 'SMS';
}

const channelCopy = (channel: 'EMAIL' | 'SMS' | undefined): string => {
  if (channel === 'SMS') return 'text messages';
  if (channel === 'EMAIL') return 'emails';
  // Token missing/unreadable - still shown, since the actual channel this
  // affects is decided server-side from the token itself, not this copy.
  // Not "these messages": the caller already prepends "these ", and a second
  // "these" here would read as "these these messages".
  return 'messages';
};

const OutreachUnsubscribe = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState<Status>('confirming');
  const [resolvedChannel, setResolvedChannel] = useState<'EMAIL' | 'SMS' | undefined>();

  const previewChannel = useMemo(() => {
    if (!token) return undefined;
    try {
      return jwtDecode<UnsubscribeTokenPayload>(token).channel;
    } catch {
      return undefined;
    }
  }, [token]);

  const handleUnsubscribe = useCallback(async () => {
    if (!token) return;
    setStatus('submitting');
    try {
      const { channel } = await postUnsubscribe(token);
      setResolvedChannel(channel);
      setStatus('done');
    } catch {
      setStatus('error');
    }
  }, [token]);

  if (!token) {
    return (
      <StandaloneShell>
        <Result
          status="error"
          title="Missing unsubscribe link"
          subTitle="This page needs the link from your email or text message - it looks like part of it didn't come through."
        />
      </StandaloneShell>
    );
  }

  if (status === 'done') {
    return (
      <StandaloneShell>
        <Result
          status="success"
          title="You're unsubscribed"
          subTitle={`You won't receive any more ${channelCopy(resolvedChannel)} from this sequence. This does not affect other communications from Avrio, such as an account or interview-related message.`}
        />
      </StandaloneShell>
    );
  }

  if (status === 'error') {
    return (
      <StandaloneShell>
        <Result
          status="error"
          title="Couldn't process this request"
          subTitle="This link may be invalid or expired. If you keep getting messages you'd like to stop, reply and let us know."
        />
      </StandaloneShell>
    );
  }

  return (
    <StandaloneShell>
      <Result
        status="info"
        title={`Unsubscribe from these ${channelCopy(previewChannel)}?`}
        subTitle="This stops future messages in this specific sequence only."
        extra={
          status === 'submitting' ? (
            <Spin />
          ) : (
            <Button type="primary" danger className={styles.confirmButton} onClick={handleUnsubscribe}>
              Yes, unsubscribe me
            </Button>
          )
        }
      />
    </StandaloneShell>
  );
};

export default OutreachUnsubscribe;
